import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Send } from "lucide-react";
import { COMPANY, DESTINATIONS, SERVICES } from "@/data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_DESTINATIONS = [...DESTINATIONS.india, ...DESTINATIONS.international].map((d) => d.name);

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0B132B]/70 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-teal-400/60";

export const ContactSection = ({ presetDestination }) => {
  const [form, setForm] = useState({ name: "", phone: "", destination: "", service: "", travel_date: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (presetDestination) {
      const isService = SERVICES.some((s) => s.title === presetDestination);
      setForm((f) => ({
        ...f,
        destination: isService ? f.destination : presetDestination,
        service: isService ? presetDestination : f.service,
      }));
    }
  }, [presetDestination]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! We'll call you back with a personalised quote.");
      setForm({ name: "", phone: "", destination: "", service: "", travel_date: "", message: "" });
    } catch {
      toast.error("Could not send right now — please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-36" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid gap-16 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-teal-300 tracking-[0.3em] text-xs uppercase mb-4">Contact for price</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">
            Tell us the destination. <span className="italic text-teal-300">We'll craft the quote.</span>
          </h2>
          <p className="mt-5 text-base text-slate-400 max-w-md leading-relaxed">
            Share a few details and our travel expert will call you back with a personalised,
            all-inclusive price — usually within a few hours.
          </p>

          <div className="mt-10 space-y-6">
            <a href={COMPANY.phoneHref} data-testid="contact-phone-link" className="flex items-start gap-4 group">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-amber-400/30 bg-amber-400/10">
                <Phone className="w-5 h-5 text-amber-400" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-500">Call us</span>
                <span className="text-base text-slate-200 group-hover:text-teal-300 transition-colors">{COMPANY.phone}</span>
              </span>
            </a>
            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="flex items-start gap-4 group">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-amber-400/30 bg-amber-400/10">
                <Mail className="w-5 h-5 text-amber-400" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-500">Email</span>
                <span className="text-base text-slate-200 group-hover:text-teal-300 transition-colors">{COMPANY.email}</span>
              </span>
            </a>
            <a href={COMPANY.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-start gap-4 group">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-teal-400/30 bg-teal-400/10">
                <MessageCircle className="w-5 h-5 text-teal-300" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-500">WhatsApp</span>
                <span className="text-base text-slate-200 group-hover:text-teal-300 transition-colors">Chat with us instantly</span>
              </span>
            </a>
            <div className="flex items-start gap-4" data-testid="contact-address">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-amber-400/30 bg-amber-400/10">
                <MapPin className="w-5 h-5 text-amber-400" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-500">Visit us</span>
                <span className="text-base text-slate-200 leading-relaxed">{COMPANY.address}</span>
              </span>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl border border-teal-500/20 bg-[#1C2541]/50 backdrop-blur-xl p-8 lg:p-10 shadow-[0_0_60px_rgba(20,184,166,0.08)]"
          data-testid="enquiry-form"
        >
          <h3 className="font-serif text-xl sm:text-2xl mb-8">Request your quote</h3>
          <div className="grid gap-5 sm:grid-cols-2">
            <input
              className={inputCls}
              placeholder="Full name *"
              value={form.name}
              onChange={set("name")}
              data-testid="enquiry-name-input"
            />
            <input
              className={inputCls}
              placeholder="Phone number *"
              value={form.phone}
              onChange={set("phone")}
              data-testid="enquiry-phone-input"
            />
            <select
              className={`${inputCls} ${form.destination ? "" : "text-slate-500"}`}
              value={form.destination}
              onChange={set("destination")}
              data-testid="enquiry-destination-select"
            >
              <option value="">Dream destination</option>
              {ALL_DESTINATIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
              <option value="Other">Somewhere else</option>
            </select>
            <select
              className={`${inputCls} ${form.service ? "" : "text-slate-500"}`}
              value={form.service}
              onChange={set("service")}
              data-testid="enquiry-service-select"
            >
              <option value="">Service needed</option>
              {SERVICES.map((s) => (
                <option key={s.id} value={s.title}>{s.title}</option>
              ))}
            </select>
            <input
              className={`${inputCls} sm:col-span-2 ${form.travel_date ? "" : "text-slate-500"}`}
              type="text"
              placeholder="Tentative travel dates (e.g. Dec 2026)"
              value={form.travel_date}
              onChange={set("travel_date")}
              data-testid="enquiry-date-input"
            />
            <textarea
              className={`${inputCls} sm:col-span-2 min-h-[110px] resize-none`}
              placeholder="Anything else — travellers, budget, special requests..."
              value={form.message}
              onChange={set("message")}
              data-testid="enquiry-message-input"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="enquiry-submit-button"
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-teal-400 py-4 text-sm font-semibold tracking-wide text-[#0B132B] transition-all duration-300 hover:bg-teal-300 hover:scale-[1.01] disabled:opacity-60"
          >
            {sending ? "Sending..." : "Send Enquiry"}
            <Send className="w-4 h-4" />
          </button>
          <p className="mt-4 text-center text-xs text-slate-500">
            Prefer talking? Call {COMPANY.phone} — we love a good travel chat.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactSection;
